import { dayjs } from "@thalamos/common";
import { CapacityOutcome, CapacityStatus, DeprivationOutcome, DolsTrackingStatus, UnassessedStatuses, } from "../types/capacityState/CapacityState.js";
/**
 * Capacity and deprivation of liberty are independent questions, and the displayed
 * status is a function of the pair. Keeping the two answers as the stored truth and
 * the label as a derivation means a half-assessed resident is an ordinary cell
 * rather than a special case, and revising wording never touches stored data.
 *
 * Exhaustive by construction: this nested `Record` is keyed by both enums, so adding
 * a value to either axis fails the build here until every combination is classified.
 * Same device as STATUS_LEVEL in mhaStatusHierarchy.ts.
 */
const MATRIX = {
    [DeprivationOutcome.NotAssessed]: {
        [CapacityOutcome.NotAssessed]: CapacityStatus.NoStatus,
        [CapacityOutcome.LacksCapacity]: CapacityStatus.AwaitingConsentAssessment,
        [CapacityOutcome.HasCapacity]: CapacityStatus.AwaitingConsentAssessment,
    },
    [DeprivationOutcome.NotDeprived]: {
        [CapacityOutcome.NotAssessed]: CapacityStatus.AwaitingCapacityAssessment,
        [CapacityOutcome.LacksCapacity]: CapacityStatus.ValidlyConsenting,
        [CapacityOutcome.HasCapacity]: CapacityStatus.ConsentingWithCapacity,
    },
    [DeprivationOutcome.Deprived]: {
        [CapacityOutcome.NotAssessed]: CapacityStatus.AwaitingCapacityAssessment,
        [CapacityOutcome.LacksCapacity]: CapacityStatus.DolsApplies,
        [CapacityOutcome.HasCapacity]: CapacityStatus.SafeguardingConcern,
    },
};
export const resolveCapacityStatus = (capacity, deprivation) => MATRIX[deprivation][capacity];
export const capacityStatusOf = (state) => resolveCapacityStatus(state.capacity.outcome, state.deprivation.outcome);
/**
 * DoLS tracking only applies once the matrix says a deprivation of liberty needs
 * authorising. Anywhere else the sub-status is meaningless and must not be shown.
 */
export const dolsTrackingApplies = (state) => capacityStatusOf(state) === CapacityStatus.DolsApplies;
/**
 * The status a newly added resident holds: present on the register, nothing
 * assessed. Not a placeholder — it is the thing the register exists to surface.
 */
export const unassessedOutcomes = {
    capacity: { outcome: CapacityOutcome.NotAssessed },
    deprivation: { outcome: DeprivationOutcome.NotAssessed },
};
/**
 * When the position now recorded was established: the later of the two axes, because
 * the picture is only as old as its most recent half.
 */
export function capacityAssessedAt(state) {
    const dates = [state.capacity.validFrom, state.deprivation.validFrom].filter((date) => !!date);
    return dates.length > 0 ? dates.sort().at(-1) : undefined;
}
/**
 * A capacity assessment has no statutory expiry — capacity is decision- and
 * time-specific, and reassessment is prompted by a change in circumstances rather than
 * by a date. So a review interval is a local policy, supplied by the caller, and its
 * absence means there is genuinely nothing to measure currency against.
 */
export function capacityDeadlines(state, reviewIntervalMonths) {
    const deadlines = [];
    if (state.urgentAuthorisation) {
        deadlines.push({
            kind: "urgent-authorisation",
            dateTime: state.urgentAuthorisation.expiryDateTime,
        });
    }
    if (state.dols?.status === DolsTrackingStatus.StandardAuthorisationGranted &&
        state.dols.expiryDateTime) {
        deadlines.push({
            kind: "standard-authorisation",
            dateTime: state.dols.expiryDateTime,
        });
    }
    // The review runs only where no authorisation does. A standard authorisation's own
    // review already requires fresh assessments, so a second clock beside it would raise
    // the same question twice.
    const assessedAt = capacityAssessedAt(state);
    if (deadlines.length === 0 &&
        reviewIntervalMonths &&
        assessedAt &&
        !UnassessedStatuses.includes(capacityStatusOf(state))) {
        deadlines.push({
            kind: "capacity-review",
            dateTime: dayjs(assessedAt)
                .add(reviewIntervalMonths, "months")
                .toISOString(),
        });
    }
    return deadlines.sort((a, b) => a.dateTime.localeCompare(b.dateTime));
}
/**
 * The status as the resident record shows it: what has been established, plus what is
 * still outstanding.
 *
 * A half-assessed resident is two facts, not one — "has capacity" is settled, "awaiting
 * the consent assessment" is not — and collapsing them into a single label loses the
 * half that tells somebody what to do next. Fully assessed residents read as one
 * composite status, which is what the register lists.
 */
export function capacityStatusPills(state) {
    const capacityAssessed = state.capacity.outcome !== CapacityOutcome.NotAssessed;
    const deprivationAssessed = state.deprivation.outcome !== DeprivationOutcome.NotAssessed;
    if (capacityAssessed && deprivationAssessed) {
        return [capacityStatusOf(state)];
    }
    if (!capacityAssessed && !deprivationAssessed) {
        return [CapacityStatus.NoStatus];
    }
    if (capacityAssessed) {
        return [
            state.capacity.outcome === CapacityOutcome.HasCapacity
                ? "Has capacity"
                : "Lacks capacity",
            CapacityStatus.AwaitingConsentAssessment,
        ];
    }
    return [
        state.deprivation.outcome === DeprivationOutcome.Deprived
            ? "Restriction identified"
            : "Consenting",
        CapacityStatus.AwaitingCapacityAssessment,
    ];
}
//# sourceMappingURL=capacityStatusMatrix.js.map